import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaTrophy, FaStar, FaCrown, FaFire, FaCalendarWeek,
    FaTicketAlt, FaArrowRight, FaClock, FaHourglassHalf,
    FaLock, FaEye, FaLayerGroup, FaTag, FaMedal,
} from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const BASE_URL = "https://api.dreamtoday.in";

/* ═══════════════════════════════════════════════════
   PALETTE
═══════════════════════════════════════════════════ */
const G = {
    gold: "#C9A84C",
    goldDeep: "#A8863A",
    goldLight: "#E8C96A",
    goldGlow: "rgba(201,168,76,0.28)",
    cream: "#FFFDF5",
    creamDeep: "#FFF8E7",
    creamMid: "#FFF1CC",
    creamBorder: "#F0D98C",
    text: "#5C4A1E",
    textMid: "#8A6E34",
    textLight: "#B89A5A",
};

/* ═══════════════════════════════════════════════════
   CATEGORY META
═══════════════════════════════════════════════════ */
const CATEGORY_META = {
    daily: {
        Icon: FaFire,
        badge: "🔥 DAILY",
        label: "Daily Campaigns",
        sub: "Fresh campaigns every day",
        iconBg: "linear-gradient(135deg,#F0D060,#C9A84C)",
        glow: "rgba(240,200,60,0.45)",
        accent: "#E8C030",
    },
    weekly: {
        Icon: FaCalendarWeek,
        badge: "⚡ WEEKLY",
        label: "Weekly Mega",
        sub: "Bigger prizes every week",
        iconBg: "linear-gradient(135deg,#D4AF37,#A8863A)",
        glow: "rgba(201,168,76,0.45)",
        accent: "#C9A84C",
    },
    monthly: {
        Icon: FaCrown,
        badge: "👑 MONTHLY",
        label: "Monthly Grand",
        sub: "Grand jackpot each month",
        iconBg: "linear-gradient(135deg,#C9A84C,#7A5C1E)",
        glow: "rgba(168,134,58,0.48)",
        accent: "#A8863A",
    },
};

const getMeta = (name = "") =>
    CATEGORY_META[name?.toLowerCase()] || {
        Icon: FaLayerGroup,
        badge: `✦ ${(name || "").toUpperCase()}`,
        label: name ? name.charAt(0).toUpperCase() + name.slice(1) : "Campaigns",
        sub: "Exclusive campaigns across various categories",
        iconBg: "linear-gradient(135deg,#C9A84C,#7A6020)",
        glow: "rgba(201,168,76,0.35)",
        accent: G.gold,
    };

/* ═══════════════════════════════════════════════════
   COUNTDOWN ENGINE
═══════════════════════════════════════════════════ */
const calcTime = (target) => {
    const diff = new Date(target) - Date.now();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
    return {
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
        expired: false,
    };
};

const useCountdown = (date) => {
    const [t, setT] = useState(() => calcTime(date));
    useEffect(() => {
        setT(calcTime(date));
        const id = setInterval(() => setT(calcTime(date)), 1000);
        return () => clearInterval(id);
    }, [date]);
    return t;
};

/* ═══════════════════════════════════════════════════
   SMART TIMER
═══════════════════════════════════════════════════ */
const SmartTimer = ({ registrationEndDate, endDate }) => {
    const regExpired = !registrationEndDate || new Date(registrationEndDate) <= new Date();
    const activeDate = regExpired ? endDate : registrationEndDate;
    const { d, h, m, s, expired } = useCountdown(activeDate);
    const isReg = !regExpired;
    const label = isReg ? "Registration closes in" : "Campaign ends in";
    const Icon = isReg ? FaClock : FaHourglassHalf;
    const col = isReg ? G.goldDeep : "#7C5C20";
    const bg = isReg
        ? `linear-gradient(135deg,${G.goldLight}44,${G.gold}22)`
        : "rgba(200,155,30,0.12)";
    const units = d > 0
        ? [{ v: d, l: "Days" }, { v: h, l: "Hrs" }, { v: m, l: "Min" }]
        : [{ v: h, l: "Hrs" }, { v: m, l: "Min" }, { v: s, l: "Sec" }];

    if (expired)
        return (
            <div
                className="flex items-center justify-center gap-2 rounded-2xl py-3"
                style={{ background: "#FFF0F0", border: "1px solid #FFCDD2" }}
            >
                <FaLock style={{ color: "#C0392B", fontSize: 10 }} />
                <span className="text-xs font-black tracking-wide" style={{ color: "#C0392B" }}>
                    Campaign Ended
                </span>
            </div>
        );

    return (
        <div
            className="rounded-2xl overflow-hidden"
            style={{ background: isReg ? G.creamMid : "#FFF4D6", border: `1px solid ${isReg ? G.creamBorder : "#DDB840"}` }}
        >
            <div className="flex items-center gap-1.5 px-3 pt-2 pb-1">
                <Icon style={{ color: col, fontSize: 9 }} />
                <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: col }}>
                    {label}
                </span>
            </div>
            <div className="flex items-center justify-center gap-1.5 px-3 pb-3">
                {units.map(({ v, l }, i) => (
                    <React.Fragment key={l}>
                        <div className="flex flex-col items-center flex-1">
                            <div
                                className="w-full flex items-center justify-center rounded-xl py-1.5"
                                style={{ background: bg, border: `1px solid ${G.creamBorder}`, minHeight: 34 }}
                            >
                                <span className="text-base font-black tabular-nums" style={{ color: col }}>
                                    {String(v).padStart(2, "0")}
                                </span>
                            </div>
                            <span className="text-[8px] font-bold mt-1 uppercase tracking-wider" style={{ color: G.textLight }}>
                                {l}
                            </span>
                        </div>
                        {i < units.length - 1 && (
                            <span className="text-sm font-black mb-5 flex-shrink-0" style={{ color: G.goldLight }}>:</span>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   PRIZE PILL
═══════════════════════════════════════════════════ */
const PrizePill = ({ prize, pos }) => {
    const medals = ["🥇", "🥈", "🥉"];
    const medal = medals[pos] || "🏅";
    return (
        <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
            style={{
                background: G.creamMid,
                color: G.goldDeep,
                border: `1px solid ${G.creamBorder}`,
            }}
        >
            <span style={{ fontSize: 10 }}>{medal}</span>
            <span className="truncate" style={{ maxWidth: 120 }}>
                {prize.prizeName}
                {prize.prizeAmount > 0 ? ` — ₹${(prize.prizeAmount || 0).toLocaleString()}` : ""}
            </span>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   STATUS BADGE
═══════════════════════════════════════════════════ */
const StatusBadge = ({ status }) => {
    const map = {
        active: { dot: "#22C55E", text: "LIVE", bg: "rgba(255,252,235,0.95)" },
        inactive: { dot: "#F59E0B", text: "SOON", bg: "rgba(255,252,235,0.95)" },
        ended: { dot: "#EF4444", text: "ENDED", bg: "rgba(255,240,240,0.95)" },
    };
    const s = map[status] || map.inactive;
    return (
        <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: s.bg, border: `1px solid ${G.creamBorder}`, backdropFilter: "blur(10px)" }}
        >
            <span
                className="rounded-full flex-shrink-0"
                style={{ width: 6, height: 6, background: s.dot, boxShadow: status === "active" ? `0 0 6px ${s.dot}` : "none" }}
            />
            <span className="text-[10px] font-black tracking-widest" style={{ color: G.text }}>{s.text}</span>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   TICKETS PROGRESS BAR
═══════════════════════════════════════════════════ */
const TicketsProgressBar = ({ total, sold }) => {
    const safeSold   = Math.min(sold  ?? 0, total);
    const available  = Math.max(total - safeSold, 0);
    const pctSold    = total > 0 ? (safeSold / total) * 100 : 0;
    const pctAvail   = 100 - pctSold;

    // colour shifts: green → amber → red as availability drops
    const barColor =
        pctAvail > 50
            ? "linear-gradient(90deg,#C9A84C,#E8C96A)"
            : pctAvail > 20
            ? "linear-gradient(90deg,#D97706,#F59E0B)"
            : "linear-gradient(90deg,#B91C1C,#EF4444)";

    const labelColor =
        pctAvail > 50 ? G.goldDeep : pctAvail > 20 ? "#92400E" : "#991B1B";

    return (
        <div className="flex flex-col gap-1.5">
            {/* label row */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                    <FaTicketAlt style={{ color: G.goldDeep, fontSize: 8 }} />
                    <span
                        className="text-[9px] font-black uppercase tracking-widest"
                        style={{ color: G.textLight }}
                    >
                        Tickets Available
                    </span>
                </div>
                <span
                    className="text-[10px] font-black tabular-nums"
                    style={{ color: labelColor }}
                >
                    {(sold || 0).toLocaleString()}
                    <span className="font-semibold text-[9px]" style={{ color: G.textLight }}>
                        /{(total || 0).toLocaleString()}
                    </span>
                </span>
            </div>

            {/* track */}
            <div
                className="relative w-full overflow-hidden"
                style={{
                    height: 7,
                    borderRadius: 99,
                    background: G.creamBorder,
                }}
            >
                {/* filled portion = tickets available (right-to-left feel: fill shrinks as sold) */}
                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${pctAvail}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        borderRadius: 99,
                        background: barColor,
                        boxShadow: `0 0 6px rgba(201,168,76,0.45)`,
                    }}
                />
                {/* shimmer streak */}
                <motion.div
                    initial={{ x: "-100%" }}
                    whileInView={{ x: "200%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: "easeInOut", delay: 0.4 }}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "40%",
                        height: "100%",
                        background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.55),transparent)",
                        borderRadius: 99,
                    }}
                />
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════
   CAMPAIGN CARD
═══════════════════════════════════════════════════ */
const CampaignCard = ({ campaign, index }) => {
    const navigate = useNavigate();
    const [imgErr, setImgErr] = useState(false);
    const [hovered, setHovered] = useState(false);
    const hasImage = campaign.images?.[0] && !imgErr;

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            onHoverStart={() => setHovered(true)}
            onHoverEnd={() => setHovered(false)}
            animate={hovered
                ? { y: -8, boxShadow: `0 22px 60px rgba(201,168,76,0.32), 0 4px 20px rgba(0,0,0,0.07)` }
                : { y: 0, boxShadow: `0 4px 24px rgba(201,168,76,0.12), 0 1px 6px rgba(0,0,0,0.04)` }
            }
            className="relative rounded-3xl overflow-hidden flex flex-col cursor-pointer"
            style={{
                background: `linear-gradient(165deg,${G.cream} 0%,${G.creamDeep} 60%,${G.creamMid} 100%)`,
                border: `1.5px solid ${hovered ? G.gold : G.creamBorder}`,
                transition: "border-color 0.28s ease",
            }}
            onClick={() => navigate(`/campaigns/${campaign._id}`)}
        >
            {/* IMAGE */}
            <div className="relative overflow-hidden flex-shrink-0" style={{ height: 220 }}>
                {hasImage ? (
                    <motion.img
                        src={`${BASE_URL}${campaign.images[0]}`}
                        alt={campaign.title}
                        onError={() => setImgErr(true)}
                        animate={{ scale: hovered ? 1.08 : 1 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div
                        className="w-full h-full flex flex-col items-center justify-center"
                        style={{ background: `linear-gradient(135deg,${G.creamMid},${G.creamBorder})` }}
                    >
                        <FaTrophy style={{ color: G.goldLight, fontSize: 46, opacity: 0.35, marginBottom: 8 }} />
                        <span className="text-xs font-medium" style={{ color: G.textLight }}>No Image</span>
                    </div>
                )}

                {/* gradient */}
                <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(to top,rgba(52,32,4,0.88) 0%,rgba(52,32,4,0.18) 46%,transparent 68%)" }}
                />

                {/* shimmer on hover */}
                <motion.div
                    className="absolute inset-0 pointer-events-none"
                    initial={{ x: "-100%", opacity: 0 }}
                    animate={hovered ? { x: "150%", opacity: 1 } : { x: "-100%", opacity: 0 }}
                    transition={{ duration: 0.65, ease: "easeInOut" }}
                    style={{ background: "linear-gradient(105deg,transparent 30%,rgba(248,220,100,0.18) 50%,transparent 70%)" }}
                />

                {/* status — top right */}
                <div className="absolute top-3.5 right-3.5">
                    <StatusBadge status={campaign.status} />
                </div>

                {/* ticket price — top left */}
                <div
                    className="absolute top-3.5 left-3.5 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                    style={{
                        background: "rgba(255,252,235,0.95)",
                        border: `1px solid ${G.creamBorder}`,
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <FaTag style={{ color: G.goldDeep, fontSize: 8 }} />
                    <span className="text-[10px] font-black" style={{ color: G.goldDeep }}>₹{campaign.price}</span>
                </div>

                {/* title */}
                <div className="absolute bottom-0 left-0 right-0 px-4 pb-3.5">
                    <h3
                        className="font-extrabold leading-snug"
                        style={{
                            color: "#FFF8E7",
                            textShadow: "0 1px 12px rgba(0,0,0,0.6)",
                            fontSize: "clamp(15px,2vw,18px)",
                        }}
                    >
                        {campaign.title}
                    </h3>
                    {campaign.description && (
                        <p
                            className="text-xs mt-0.5 font-medium line-clamp-1"
                            style={{ color: "rgba(255,248,231,0.68)" }}
                        >
                            {campaign.description}
                        </p>
                    )}
                </div>
            </div>

            {/* BODY */}
            <div className="flex flex-col flex-1 p-4 gap-3">

                {/* TIMER */}
                <SmartTimer
                    registrationEndDate={campaign.registrationEndDate}
                    endDate={campaign.endDate}
                />

                {/* PRIZES */}
                {campaign.prizes?.length > 0 && (
                    <div>
                        <p className="font-black uppercase tracking-widest mb-1.5" style={{ color: G.textLight, fontSize: 9 }}>
                            Prizes
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {campaign.prizes.slice(0, 2).map((p, i) => (
                                <PrizePill key={p._id} prize={p} pos={i} />
                            ))}
                            {campaign.prizes.length > 2 && (
                                <span
                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold"
                                    style={{ background: G.creamMid, color: G.textMid, border: `1px solid ${G.creamBorder}` }}
                                >
                                    +{campaign.prizes.length - 2} more
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* TICKETS PROGRESS BAR */}
                <div style={{ borderTop: `1px solid ${G.creamBorder}`, paddingTop: 12 }}>
                    <TicketsProgressBar
                        total={campaign.noOfTickets}
                        sold={campaign.remainingTickets}
                    />
                </div>

                {/* STATS ROW */}
                <div
                    className="flex items-end justify-between pt-3"
                    style={{ borderTop: `1px solid ${G.creamBorder}` }}
                >
                    <div>
                        <p className="font-semibold uppercase tracking-wider mb-0.5" style={{ color: G.textLight, fontSize: 9 }}>
                            Total Tickets
                        </p>
                        <p className="text-sm font-extrabold" style={{ color: G.text }}>
                            {(campaign.noOfTickets || 0).toLocaleString()}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="font-semibold uppercase tracking-wider mb-0.5" style={{ color: G.textLight, fontSize: 9 }}>
                            Winners
                        </p>
                        <p className="text-sm font-extrabold" style={{ color: G.text }}>
                            {(campaign.noOfWinners || 0).toLocaleString()}
                        </p>
                    </div>
                </div>

                {/* CTA */}
                <motion.button
                    whileTap={{ scale: 0.97 }}
                    animate={hovered
                        ? { background: `linear-gradient(135deg,${G.goldLight},${G.gold},${G.goldDeep})` }
                        : { background: `linear-gradient(135deg,${G.goldLight} 0%,${G.gold} 50%,${G.goldDeep} 100%)` }
                    }
                    className="w-full rounded-2xl font-extrabold text-sm tracking-wide
            flex items-center justify-center gap-2"
                    style={{
                        color: "#3B2A0A",
                        boxShadow: hovered ? `0 8px 28px rgba(201,168,76,0.45)` : `0 4px 18px ${G.goldGlow}`,
                        paddingTop: 13,
                        paddingBottom: 13,
                        border: "none",
                        cursor: "pointer",
                        transition: "box-shadow 0.28s ease",
                    }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/campaigns/${campaign._id}`); }}
                >
                    <FaEye />
                    View Campaign
                    <motion.span animate={{ x: hovered ? 3 : 0 }} transition={{ duration: 0.2 }}>
                        <FaArrowRight style={{ fontSize: 10 }} />
                    </motion.span>
                </motion.button>
            </div>

            {/* glow border bloom */}
            <motion.div
                className="absolute inset-0 rounded-3xl pointer-events-none"
                animate={hovered
                    ? { opacity: 1, boxShadow: `inset 0 0 0 1.5px ${G.goldLight}` }
                    : { opacity: 0, boxShadow: "inset 0 0 0 0px transparent" }
                }
                transition={{ duration: 0.28 }}
            />
        </motion.div>
    );
};

/* ═══════════════════════════════════════════════════
   SKELETON
═══════════════════════════════════════════════════ */
const SkeletonCard = () => (
    <div
        className="rounded-3xl overflow-hidden animate-pulse"
        style={{ background: G.creamDeep, border: `1.5px solid ${G.creamBorder}`, minHeight: 460 }}
    >
        <div style={{ height: 220, background: G.creamMid }} />
        <div className="p-4 flex flex-col gap-3">
            <div className="h-[68px] rounded-2xl" style={{ background: G.creamMid }} />
            <div className="h-4 rounded-lg w-2/3" style={{ background: G.creamMid }} />
            <div className="h-3 rounded-lg w-1/2" style={{ background: G.creamBorder }} />
            <div className="mt-2 h-11 rounded-2xl" style={{ background: G.creamMid }} />
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
const CategoryCampaigns = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();

    const [campaigns, setCampaigns] = useState([]);
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!categoryId) return;
        setLoading(true);
        setError("");

        fetch(`${BASE_URL}/api/admin/allcampaigns/${categoryId}`)
            .then((r) => r.json())
            .then((d) => {
                if (d.success) {
                    setCampaigns(d.campaigns || []);
                    /* derive category from first campaign's populated field */
                    if (d.campaigns?.[0]?.campCateId) {
                        setCategory(d.campaigns[0].campCateId);
                    }
                } else {
                    throw new Error(d.message || "Failed to load campaigns");
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [categoryId]);

    const meta = getMeta(category?.name || "");
    const { Icon, badge, label, sub, iconBg, glow } = meta;

    return (
        <>
        <Navbar/>
            <section
                className="relative overflow-hidden min-h-screen"
                style={{
                    background: `linear-gradient(180deg,${G.cream} 0%,${G.creamDeep} 50%,${G.cream} 100%)`,
                    paddingTop: "clamp(48px,8vw,96px)",
                    //paddingBottom: "clamp(48px,8vw,96px)",
                }}
            >
                {/* BG orbs */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
                    {[{ top: -180, right: -180 }, { bottom: -180, left: -180 }].map((pos, i) => (
                        <div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                ...pos, width: 520, height: 520,
                                background: "radial-gradient(circle,rgba(240,217,140,0.24),transparent 70%)",
                            }}
                        />
                    ))}
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* ── BACK BUTTON ── */}
                    <motion.button
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 mb-8 text-sm font-bold rounded-2xl px-4 py-2.5"
                        style={{
                            background: G.creamMid,
                            color: G.goldDeep,
                            border: `1px solid ${G.creamBorder}`,
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M19 12H5M5 12l7 7M5 12l7-7" />
                        </svg>
                        Back
                    </motion.button>

                    {/* ── PAGE HEADER ── */}
                    <div className="text-center mb-12 sm:mb-16">

                        {/* category icon pill */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.88 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-3 rounded-2xl mb-6 px-5 py-3"
                            style={{
                                background: G.creamMid,
                                border: `1.5px solid ${G.creamBorder}`,
                                boxShadow: `0 4px 20px ${glow}`,
                            }}
                        >
                            <div
                                className="rounded-xl flex items-center justify-center shadow"
                                style={{ width: 36, height: 36, background: iconBg }}
                            >
                                <Icon style={{ color: "#fff", fontSize: 16 }} />
                            </div>
                            <div className="text-left">
                                <p className="text-xs font-black uppercase tracking-widest" style={{ color: G.goldDeep }}>
                                    {badge}
                                </p>
                                <p className="text-[10px] font-medium" style={{ color: G.textLight }}>{sub}</p>
                            </div>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.55 }}
                            className="font-extrabold"
                            style={{
                                color: G.text,
                                fontSize: "clamp(28px,4.5vw,48px)",
                                lineHeight: 1.12,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {label} Campaigns
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.16 }}
                            style={{ color: G.textLight, fontSize: 14, maxWidth: 440, margin: "10px auto 0" }}
                        >
                            Browse all active {label.toLowerCase()} campaigns and grab your winning ticket.
                        </motion.p>

                        {/* ornamental divider */}
                        <div className="flex items-center justify-center gap-2.5 mt-7">
                            <div style={{ height: 1, width: "clamp(32px,6vw,96px)", background: `linear-gradient(to right,transparent,${G.creamBorder})` }} />
                            <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
                            <div style={{ height: 1, width: 20, background: G.creamBorder }} />
                            <FaTrophy style={{ color: G.gold, fontSize: 12 }} />
                            <div style={{ height: 1, width: 20, background: G.creamBorder }} />
                            <FaStar style={{ color: G.goldLight, fontSize: 9 }} />
                            <div style={{ height: 1, width: "clamp(32px,6vw,96px)", background: `linear-gradient(to left,transparent,${G.creamBorder})` }} />
                        </div>

                        {/* count badge */}
                        {!loading && !error && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.22 }}
                                className="inline-flex items-center gap-1.5 mt-5 px-3.5 py-1.5 rounded-full text-xs font-black"
                                style={{
                                    background: G.creamMid,
                                    color: G.goldDeep,
                                    border: `1px solid ${G.creamBorder}`,
                                }}
                            >
                                <FaTicketAlt style={{ fontSize: 9 }} />
                                {campaigns.length} Campaign{campaigns.length !== 1 ? "s" : ""} Available
                            </motion.div>
                        )}
                    </div>

                    {/* ── CONTENT ── */}
                    <AnimatePresence mode="wait">

                        {/* LOADING */}
                        {loading && (
                            <motion.div
                                key="loading"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                className="grid gap-6 lg:gap-7"
                                style={{ gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,300px),1fr))" }}
                            >
                                {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
                            </motion.div>
                        )}

                        {/* ERROR */}
                        {!loading && error && (
                            <motion.div
                                key="error"
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                className="text-center rounded-3xl p-14 border"
                                style={{ background: "#FFF5F5", borderColor: "#FFCDD2" }}
                            >
                                <p className="text-lg font-bold mb-1" style={{ color: "#C62828" }}>
                                    ⚠️ Failed to load campaigns
                                </p>
                                <p className="text-sm" style={{ color: "#C62828", opacity: 0.7 }}>{error}</p>
                            </motion.div>
                        )}

                        {/* EMPTY */}
                        {!loading && !error && campaigns.length === 0 && (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                className="text-center rounded-3xl p-16 border flex flex-col items-center"
                                style={{ background: G.creamDeep, borderColor: G.creamBorder }}
                            >
                                <div
                                    className="rounded-2xl flex items-center justify-center mb-5 shadow"
                                    style={{ width: 64, height: 64, background: iconBg }}
                                >
                                    <Icon style={{ color: "#fff", fontSize: 26 }} />
                                </div>
                                <p className="text-base font-extrabold mb-1" style={{ color: G.text }}>
                                    No {label} Campaigns Yet
                                </p>
                                <p className="text-sm" style={{ color: G.textLight }}>
                                    Check back soon — new campaigns are added regularly!
                                </p>
                            </motion.div>
                        )}

                        {/* CARDS GRID */}
                        {!loading && !error && campaigns.length > 0 && (
                            <motion.div
                                key="cards"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                className="grid gap-6 lg:gap-7"
                                style={{ gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,300px),1fr))" }}
                            >
                                {campaigns.map((c, i) => (
                                    <CampaignCard key={c._id} campaign={c} index={i} />
                                ))}
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </section>
        </>
    );
};

export default CategoryCampaigns;